import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  Button,
  Card,
  Form,
  Spinner,
  Alert,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { Box } from "./domainStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";
import { FaGlobe, FaCalendarAlt, FaServer, FaShieldAlt } from "react-icons/fa";

interface ApiResponse {
  success: boolean;
  message: string;
  data: DomainInfo | string;
}

interface DomainInfo {
  domainName: string;
  createOn: string;
  updateOn: string;
  expirationDate: string;
  age: string;
  registryDomainId: string;
  registrarWhoisServer: string;
  registrarUrl: string;
  registrar: string;
  registrarIanaId: string;
  registrarAbuseContactEmail: string;
  registrarAbuseContactPhone: string;
  domainStatus: string | string[];
  nameServer: string[];
  dnssec: string;
}

const renderKeyValue = (label: string, value: React.ReactNode) => (
  <div className="d-flex justify-content-between align-items-start mb-2 small">
    <span className="fw-semibold text-muted">{label}:</span>
    <span className="text-break ms-2">{value || <em className="text-muted">N/A</em>}</span>
  </div>
);

export const DomainChecker: React.FC = () => {
  const [domainName, setDomainName] = useState("");
  const [domainInfo, setDomainInfo] = useState<DomainInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [domainInfo, error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setDomainInfo(null);

    let trimmed = domainName.trim();
    if (!trimmed) return;

    // Auto prepend https:// if missing
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
      setDomainName(trimmed);
    }

    try {
      new URL(trimmed);
    } catch {
      setError("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    setLoading(true);
    try {
      const { hostname } = new URL(trimmed);
      const cleanUrl = hostname.replace(/^www\./, "");
      const baseUrl = process.env.REACT_APP_BASE_API || "";

      const response = await axios.get<ApiResponse>(
        `${baseUrl}/api/domain-checker/${cleanUrl}`,
        { timeout: 15000 }
      );

      if (response.data?.data && typeof response.data.data === "object") {
        setDomainInfo(response.data.data as DomainInfo);
      } else {
        throw new Error("Domain information not found");
      }
    } catch (err: any) {
      const userError =
        err.response?.data?.error ||
        (err.message.includes("timeout")
          ? "Request timed out—try again later."
          : "Something went wrong. Please try again.");
      setError(userError);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | undefined) =>
    dateStr ? moment(dateStr).format("DD MMM YYYY, HH:mm") : <em className="text-muted">N/A</em>;

  const renderStatusBadges = (status: string | string[] | undefined) => {
    if (!status) return <Badge bg="secondary">N/A</Badge>;
    const statuses = Array.isArray(status) ? status : [status];
    return statuses.map((s, idx) => (
      <Badge key={idx} bg="info" className="me-1 mb-1 d-inline-block" style={{ maxWidth: '100%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
        {s}
      </Badge>
    ));
  };

  return (
    <Box>
      <div className="container py-4 text-center">
        {!isInIframe && (
          <ToolsHeader
            title="Domain Age Checker"
            description="Quickly find out when a website was created and how old it is — fast, accurate, and free."
          />
        )}

        <Form onSubmit={handleSubmit} className="mt-4">
          <div className="d-flex justify-content-center">
            <div style={{ maxWidth: "700px", width: "100%" }}>
              <Form.Control
                type="text"
                value={domainName}
                onChange={(e) => {
                  setError("");
                  setDomainName(e.target.value);
                }}
                placeholder="Enter domain (e.g., webfx.com)"
                className="fs-5 py-3 px-4 border-0 shadow-sm "
                style={{
                  backgroundColor: "#f9f9f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              />
              <div className="mt-3">
                <Button
                  type="submit"
                  variant="success"
                  disabled={loading || !domainName.trim()}
                  className="px-5 py-2 "
                  style={{
                    background: "#8CC540",
                    border: "none",
                    fontSize: "1.1rem",
                    fontWeight: 600,
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Checking...
                    </>
                  ) : (
                    "Check Domain Age"
                  )}
                </Button>
              </div>
              {error && (
                <Alert variant="danger" className="mt-3 small rounded-3">
                  {error}
                </Alert>
              )}
            </div>
          </div>
        </Form>

        {domainInfo && (
          <Card
            className="shadow border-0 rounded-4 mt-5 mx-auto"
            style={{ maxWidth: "900px" }}
          >
            <Card.Header
              className="text-white rounded-top-4"
              style={{
                background: "#8CC540",
              }}
            >
              <h5 className="mb-0">
                <FaGlobe className="me-2" />
                Domain Overview
              </h5>
            </Card.Header>

            <Card.Body className="p-4">
              <Row className="text-center gy-4">
                <Col md={3} xs={6}>
                  <FaCalendarAlt className="text-success mb-2" size={22} />
                  <div className="fw-semibold small text-muted">Created On</div>
                  <div className="text-break">{formatDate(domainInfo.createOn)}</div>
                </Col>
                <Col md={3} xs={6}>
                  <FaCalendarAlt className="text-warning mb-2" size={22} />
                  <div className="fw-semibold small text-muted">Updated On</div>
                  <div className="text-break">{formatDate(domainInfo.updateOn)}</div>
                </Col>
                <Col md={3} xs={6}>
                  <FaCalendarAlt className="text-danger mb-2" size={22} />
                  <div className="fw-semibold small text-muted">Expires On</div>
                  <div className="text-break">{formatDate(domainInfo.expirationDate)}</div>
                </Col>
                <Col md={3} xs={6}>
                  <FaShieldAlt className="text-primary mb-2" size={22} />
                  <div className="fw-semibold small text-muted">Domain Age</div>
                  <div className="text-break">{domainInfo.age}</div>
                </Col>
              </Row>

              <hr className="my-4" />

              <Row className="text-start">
                <Col md={6}>
                  {renderKeyValue("Registry Domain ID", domainInfo.registryDomainId)}
                  {renderKeyValue("Registrar", domainInfo.registrar)}
                  {renderKeyValue("Registrar IANA ID", domainInfo.registrarIanaId)}
                  {renderKeyValue("Registrar URL", domainInfo.registrarUrl)}
                </Col>
                <Col md={6}>
                  {renderKeyValue("WHOIS Server", domainInfo.registrarWhoisServer)}
                  {renderKeyValue("Abuse Email", domainInfo.registrarAbuseContactEmail)}
                  {renderKeyValue("Abuse Phone", domainInfo.registrarAbuseContactPhone)}
                  {renderKeyValue("DNSSEC", domainInfo.dnssec)}
                </Col>
              </Row>

              <hr className="my-4" />

              <div style={{ width: "100%", textAlign: "left" }}>
  <h6 className="fw-bold mb-2 text-start">Domain Status</h6>
  <div
    className="mb-3 d-flex flex-wrap"
    style={{
      justifyContent: "flex-start",
      alignItems: "flex-start",
      width: "100%",
    }}
  >
    {renderStatusBadges(domainInfo.domainStatus)}
  </div>
</div>

              <h6 className="fw-bold mb-2 text-start">Name Servers</h6>
              <ul className="small text-start">
                {domainInfo.nameServer.length > 0 ? (
                  domainInfo.nameServer.map((ns, i) => (
                    <li key={i} className="text-break">
                      {ns}
                    </li>
                  ))
                ) : (
                  <em className="text-muted">N/A</em>
                )}
              </ul>
            </Card.Body>
          </Card>
        )}
      </div>
    </Box>
  );
};